import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardContent, MatCardTitle } from '@angular/material/card';
import { MatError, MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { RouterLink } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { looksLikeOrgKey } from '@processpuzzle/auth/domain';
import { CustomerAccountStore } from '../domain/customer-account.store';

/**
 * Asks which organization to open, when the browser has not remembered one.
 *
 * **Why there is a prompt at all.** The portal has no organization key in its URL and no principal
 * to derive one from, so the only sources are what was remembered from a sign-up on this browser
 * and what the customer can tell it. A customer on a new device, after clearing site data, or after
 * signing up somewhere else has nothing remembered — and showing them an error for that would be
 * blaming them for a fact about their browser.
 *
 * **The key is validated against the slug rule before the request**, unlike the funnel's own key
 * field. The two are asking opposite questions: the funnel asks "is this key free", where a
 * malformed value is a useful answer from the server, and this asks "show me this organization",
 * where a malformed value can only be a 404 and a round trip to learn that is wasted.
 *
 * There is deliberately no "find my organization" by email. That would be an unauthenticated lookup
 * from an email address to a tenant, on a surface with no principal — which is a customer
 * enumeration endpoint however it is worded.
 */
@Component({
  selector: 'pp-organization-key-prompt',
  imports: [ReactiveFormsModule, MatCard, MatCardTitle, MatCardContent, MatFormField, MatLabel, MatError, MatHint, MatInput, MatButton, RouterLink, TranslocoDirective],
  template: `
    <ng-container *transloco="let t; prefix: 'platform_subscription.portal.key_prompt'">
      <mat-card class="key-prompt-card">
        <mat-card-title>{{ t('title') }}</mat-card-title>
        <mat-card-content>
          <p>{{ t('lead') }}</p>
          <form [formGroup]="form" class="key-prompt-form" (ngSubmit)="open()">
            <mat-form-field>
              <mat-label>{{ t('key') }}</mat-label>
              <input id="portal-org-key" matInput formControlName="orgKey" autocomplete="off" spellcheck="false" test-id="portal-org-key" (input)="store.clearNotFound()" />
              <mat-hint>{{ t('keyHint') }}</mat-hint>
              @if (form.controls.orgKey.hasError('required') && form.controls.orgKey.touched) {
                <mat-error>{{ t('keyRequired') }}</mat-error>
              }
              @if (form.controls.orgKey.hasError('pattern') && form.controls.orgKey.touched) {
                <mat-error>{{ t('keyInvalid') }}</mat-error>
              }
            </mat-form-field>
            <button id="portal-org-key-submit" type="submit" mat-raised-button color="primary" test-id="portal-org-key-submit" [disabled]="form.invalid">
              {{ t('open') }}
            </button>
          </form>

          @if (store.notFound()) {
            <p class="key-prompt-error" test-id="portal-org-key-not-found">{{ t('notFound') }}</p>
            <p class="key-prompt-note">
              {{ t('noAccount') }}
              <a routerLink="/signup" test-id="portal-to-signup">{{ t('toSignup') }}</a>
            </p>
          }
        </mat-card-content>
      </mat-card>
    </ng-container>
  `,
  styles: [
    `
      .key-prompt-card {
        max-width: 720px;
      }

      .key-prompt-form {
        display: flex;
        align-items: flex-start;
        gap: 12px;
      }

      .key-prompt-form mat-form-field {
        flex: 1;
      }

      .key-prompt-error {
        color: var(--mat-sys-error, #b3261e);
      }

      .key-prompt-note {
        font-size: 0.9rem;
        opacity: 0.8;
      }
    `,
  ],
})
export class OrganizationKeyPromptComponent {
  readonly store = inject(CustomerAccountStore);
  private readonly formBuilder = inject(FormBuilder);

  /**
   * The same slug rule the backend enforces, as a pattern.
   *
   * Core's `looksLikeOrgKey` scans rather than using this regex, on the grounds that the nested
   * repetition makes engines backtrack on a long adversarial input. That reasoning holds for a value
   * arriving from a URL; here the input is a form field a person is typing, bounded by `maxlength`
   * on the model, and `Validators.pattern` is what a reactive form can use. {@link open} checks with
   * the scanning helper as well, so the request is never made for a value the helper rejects.
   */
  readonly form = this.formBuilder.nonNullable.group({
    orgKey: ['', [Validators.required, Validators.pattern(/^[a-z0-9]+(-[a-z0-9]+)*$/)]],
  });

  open(): void {
    if (this.form.invalid) return;
    const orgKey = this.form.controls.orgKey.value.trim();
    if (!looksLikeOrgKey(orgKey)) return;
    this.store.openOrganization(orgKey);
  }
}
